// Copyright 2020 Sorama B.V.

import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { MeasurementProcessorTypes } from "datatypes/MeasurementProcessorTypes";
import { GenericMeasurement, MeasurementPartType, Point3D, Range } from "datatypes/SoramaWebApi";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { ArrayProperties, getArrayProperties } from "libraries/Math/ArrayProperties";
import { IWorkflowModuleConfig } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { IWorkflowModuleController } from "libraries/WorkflowFramework/Interfaces/IWorkflowRouter";
import { ComputationModes } from "modules/workflows/Analysis/Spectrum/SpectrumModuleModes";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { IBeamformingProcessingService } from "services/SoundProcessing/IBeamformingProcessingService";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";

export class RecommendedAnalysis {
    public analysisType: IWorkflowModuleConfig;
    public reason: string;
    public guidanceLink?: string;
}

/***
 * Based on: https://sorama.atlassian.net/wiki/spaces/UI/pages/889815237/Visual+cues+in+portal+modules
 */
export class RecommendationEngine {
    private measurements: GenericMeasurement[] = [];
    private modules: IWorkflowModuleController[] = [];

    /**
     *
     * @param processorResolver If this input is given, the engine will also check if all measurement are stored correctly in the cloud and/or locally
     */
    constructor(private readonly processorResolver?: IMeasurementProcessorResolver) {}

    /**
     * If given, the recommendation is based on the current open modules. Optional input.
     * @param modules
     */
    public withModules(modules: IWorkflowModuleController[]): RecommendationEngine {
        this.modules.push(...modules);
        return this;
    }

    /**
     * The measurement on which the recommendation is based
     * @param measurement
     */
    public forMeasurement(measurement: GenericMeasurement): RecommendationEngine {
        if (measurement) {
            this.forMeasurements([measurement]);
        }
        return this;
    }

    /**
     * The measurements on which the recommendation is based
     * @param measurement
     */
    public forMeasurements(genericMeasurements: GenericMeasurement[]): RecommendationEngine {
        if (genericMeasurements) {
            this.measurements.push(...genericMeasurements.filter((x) => x != undefined));
        }
        return this;
    }

    public getFrequencyBandRecommendations(): Range[] {
        // For iV64 measurements, for now, we manually set the range based on what our correction curve is made for
        const containsIV64Measurements = this.measurements.some((x) => x.hasUploadedRawSorFile || x.sorId);
        if (containsIV64Measurements) {
            return [ComputationModes.getValidFrequencyRange(ComputationModes.iV64)];
        }

        const result: Range = { from: Number.MIN_SAFE_INTEGER, to: Number.MAX_SAFE_INTEGER };

        for (const measurement of this.measurements) {
            const audioParts = measurement.parts.filter((x) => x.measurementType == MeasurementPartType.Audio);

            if (audioParts.length == 0) continue; // There is no audio, so ignore

            const uraProps = this.getArrayProperties(this.measurements[0]);
            const nearFieldRecommended = this.isNearfieldRecommended(measurement);

            let range: Range;
            if (nearFieldRecommended) {
                range = <Range>{
                    from: (0.5 * 0.15 * 343) / uraProps.maxSensorDistance,
                    to: 343 / (2 * uraProps.spacing),
                };
            } else if (this.isFarfieldRecommended(measurement)) {
                range = <Range>{
                    from: (0.5 * 1.3 * 343) / uraProps.maxSensorDistance,
                    to: Math.min((0.5 * 343) / uraProps.spacing, audioParts[0].device.sampleRate / 2),
                };
            } else {
                // For generic analysis, the whole range is interesting. Maybe we can do something smart with computed measurements
                range = <Range>{
                    from: 0,
                    to: audioParts[0].device.sampleRate / 2,
                };
            }

            result.from = Math.max(result.from, range.from);
            result.to = Math.min(result.to, range.to);
        }

        if (result.from == Number.MIN_SAFE_INTEGER || result.to == Number.MAX_SAFE_INTEGER) {
            return [];
        }

        return [result];
    }

    /**
     * Get all analysis types and their availability based on the current state of the engine
     */
    public async getAnalysisTypes(): Promise<IWorkflowModuleConfig[]> {
        const result: IWorkflowModuleConfig[] = [];

        const canProcessGenericAnalysis = this.processorResolver
            ? await (
                  await this.processorResolver.canProcess(this.measurements, ISpectralProcessingService)
              ).every((x) => x.available)
            : true;

        result.push(
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.TimeModule,
                canProcessGenericAnalysis,
                "Cannot process the measurement"
            )
        );
        result.push(
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.SpectrumModule,
                canProcessGenericAnalysis,
                "Cannot process the measurement"
            )
        );

        if (this.measurements.length <= 1) {
            // Also check modules which cannot compare measurements
            result.push(
                this.setAnalysisTypeState(
                    AnalysisWorkflowModuleCatalog.SpectrogramModule,
                    canProcessGenericAnalysis,
                    "Cannot process the measurement"
                )
            );

            const nearfieldIsSupported =
                this.measurements.length == 0 || this.isNearfieldSupported(this.measurements[0]);
            result.push(
                this.setAnalysisTypeState(
                    AnalysisWorkflowModuleCatalog.NearfieldModule,
                    nearfieldIsSupported,
                    "Near Field is not supported for this measurement"
                )
            );

            const farfieldIsSupported = this.measurements.length == 0 || this.isFarFieldSupported(this.measurements[0]);
            if (farfieldIsSupported) {
                const canProcess = this.processorResolver
                    ? await (
                          await this.processorResolver.canProcess(this.measurements, IBeamformingProcessingService)
                      ).every((x) => x.available)
                    : true;
                result.push(
                    this.setAnalysisTypeState(
                        AnalysisWorkflowModuleCatalog.FarfieldModule,
                        canProcess,
                        "Cannot process the measurement"
                    )
                );
            } else {
                result.push(
                    this.setAnalysisTypeState(
                        AnalysisWorkflowModuleCatalog.FarfieldModule,
                        false,
                        "Far Field is not supported for this measurement"
                    )
                );
            }
        }

        // Check if we can do psychoacoustics
        const hasAudio = this.measurements.every((m) =>
            m.parts.some((p) => p.measurementType === MeasurementPartType.Audio)
        );
        const hasMicPositions = this.measurements.every(
            (m) => MeasurementExtensions.getMicrophonePositionsForMeasurement(m).length > 0
        );

        result.push(
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.PsychoAcousticsModule,
                hasAudio && hasMicPositions,
                "Psychoacoustics is not supported for this measurement"
            )
        );

        return result;
    }

    /**
     * Set the state of an analysis type
     * @param analysisType the analysis type to set
     * @param enabled  if the analysis should be enabled
     * @param disabledReason the reason why the analysis type is disabled. Only used if enabled is false
     */
    private setAnalysisTypeState(analysisType: IWorkflowModuleConfig, enabled = false, disabledReason?: string) {
        analysisType.disabledReason = enabled ? null : disabledReason;
        analysisType.isDisabled = !enabled;
        return analysisType;
    }

    public getRecommendedNextAnalysisStep(hasFrequencySelected: boolean): RecommendedAnalysis {
        if (this.measurements.length == 0) {
            return <RecommendedAnalysis>{
                analysisType: AnalysisWorkflowModuleCatalog.SpectrumModule,
                reason: "A spectrum is a good starting point for an analysis",
            };
        }

        if (this.measurements.length > 1) {
            return <RecommendedAnalysis>{
                analysisType: AnalysisWorkflowModuleCatalog.SpectrumModule,
                reason: "Find interesting frequency areas by comparing multiple measurements",
            };
        }

        const nearfieldIsSupported = this.measurements.length == 0 || this.isNearfieldSupported(this.measurements[0]);
        const farfieldIsSupported = this.measurements.length == 0 || this.isFarFieldSupported(this.measurements[0]);

        // Assume that near-field and far-field measurements are not stationary
        const nearFieldRecommended = this.isNearfieldRecommended(this.measurements[0]);

        if (nearFieldRecommended && !hasFrequencySelected) {
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.NearfieldModule,
                false,
                "Select a frequency band first in the spectrum module"
            );

            if (farfieldIsSupported) {
                this.setAnalysisTypeState(
                    AnalysisWorkflowModuleCatalog.FarfieldModule,
                    false,
                    "Select a frequency band first in the spectrum module"
                );
            }

            return <RecommendedAnalysis>{
                analysisType: AnalysisWorkflowModuleCatalog.SpectrumModule,
                reason: "A spectrum is a good starting point for a stationary source",
            };
        }

        if (!nearFieldRecommended && !hasFrequencySelected) {
            this.setAnalysisTypeState(
                AnalysisWorkflowModuleCatalog.FarfieldModule,
                false,
                "Select a frequency band first in the spectrogram module"
            );

            if (nearfieldIsSupported) {
                this.setAnalysisTypeState(
                    AnalysisWorkflowModuleCatalog.NearfieldModule,
                    false,
                    "Select a frequency band first in the spectrogram module"
                );
            }

            return <RecommendedAnalysis>{
                analysisType: AnalysisWorkflowModuleCatalog.SpectrogramModule,
                reason: "A spectrogram is a good starting point for a non-stationary source",
            };
        }

        if (nearFieldRecommended && hasFrequencySelected) {
            this.setAnalysisTypeState(AnalysisWorkflowModuleCatalog.NearfieldModule, true);

            if (farfieldIsSupported) {
                this.setAnalysisTypeState(AnalysisWorkflowModuleCatalog.FarfieldModule, true);
            }

            return <RecommendedAnalysis>{
                analysisType: AnalysisWorkflowModuleCatalog.NearfieldModule,
                reason: "The measurement is performed in the near field. Let's start analyzing.",
            };
        }

        if (!nearFieldRecommended && hasFrequencySelected && farfieldIsSupported) {
            this.setAnalysisTypeState(AnalysisWorkflowModuleCatalog.FarfieldModule, true);

            if (nearfieldIsSupported) {
                this.setAnalysisTypeState(AnalysisWorkflowModuleCatalog.NearfieldModule, true);
            }

            return <RecommendedAnalysis>{
                analysisType: AnalysisWorkflowModuleCatalog.FarfieldModule,
                reason: "The measurement is performed in the far field. Let's start analyzing.",
            };
        }
        return <RecommendedAnalysis>{
            analysisType: AnalysisWorkflowModuleCatalog.TimeModule,
            reason: "Analyze the signals as measured by the microphones or probes",
        };
    }

    private getArrayProperties(measurement: GenericMeasurement): ArrayProperties {
        const positions: Point3D[] = [];

        for (const part of measurement.parts.filter((x) => x.measurementType == MeasurementPartType.Audio)) {
            if (part.device && part.device.sensorPositions) {
                positions.push(
                    ...part.device.sensorPositions.map(
                        (x) =>
                            <Point3D>{
                                x: x.x + (part.position ? part.position.x : 0),
                                y: x.y + (part.position ? part.position.y : 0),
                                z: x.z + (part.position ? part.position.z : 0),
                            }
                    )
                );
            }
        }

        return getArrayProperties(positions);
    }

    private isFarFieldSupported(measurement: GenericMeasurement): boolean {
        if (
            measurement.isComputedMeasurement &&
            measurement.computedMeasurementProperties.ProcessorType != MeasurementProcessorTypes.timeRange
        )
            return false;

        const audioParts = measurement.parts.filter((x) => x.measurementType == MeasurementPartType.Audio);

        if (audioParts.length != 1) {
            return false;
        }

        const uraProps = this.getArrayProperties(measurement);

        if (uraProps.width == 0 && uraProps.height == 0) {
            // There is no array dimension, so we cannot do any beamforming
            return false;
        }

        return true;
    }

    private isFarfieldRecommended(measurement: GenericMeasurement): boolean {
        if (this.isNearfieldRecommended(measurement) || !this.isFarFieldSupported(measurement)) return false;

        const uraProps = this.getArrayProperties(measurement);

        // As a "rule of thumb" we're using half the wavelength
        if (measurement.distance <= 0.5 * Math.min(uraProps.height, uraProps.width)) {
            return false;
        }

        return true;
    }

    private isNearfieldSupported(measurement: GenericMeasurement): boolean {
        if (
            measurement.isComputedMeasurement &&
            measurement.computedMeasurementProperties.ProcessorType != MeasurementProcessorTypes.timeRange
        )
            return false;

        const audioParts = measurement.parts.filter((x) => x.measurementType == MeasurementPartType.Audio);

        if (audioParts.length == 0) {
            return false;
        }

        const arrayProps = this.getArrayProperties(measurement);

        if (!arrayProps.isUra) {
            return false;
        }

        if (arrayProps.width / arrayProps.spacing < 8 || arrayProps.width / arrayProps.spacing < 8) {
            // We need at least 8 sensors in both directions
            return false;
        }

        return true;
    }

    private isNearfieldRecommended(measurement: GenericMeasurement): boolean {
        if (!this.isNearfieldSupported(measurement)) {
            return false;
        }

        const uraProps = this.getArrayProperties(measurement);

        // As a "rule of thumb" we're using half the wavelength
        if (measurement.distance <= 0.5 * Math.min(uraProps.height, uraProps.width)) {
            return true;
        }

        return false;
    }
}
